import { Edit2, MapPin, Trash2, User, MoreHorizontal, ToggleLeft, ToggleRight } from "lucide-react";
import moment from "moment";

import DataTable from "../../../components/shared/DataTable";
import { Button } from "../../../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Switch } from "../../../components/ui/switch.jsx";
import { Label } from "../../../components/ui/label.jsx";

const ContractorTable = ({
  contractors,
  onEdit,
  onDelete,
  permissions,
  sortBy,
  direction,
  onSort,
  showDeleted,
  setShowDeleted,
  setContractorPage
}) => {
  const columns = [
    {
      header: "Name",
      accessor: "contractorName",
      isPrimary: true,
      render: (value, contractor) => (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-yale-blue-900 dark:text-slate-100">{value}</span>
          {contractor.deleted && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
              Deleted
            </span>
          )}
        </div>
      )
    },
    {
      header: "Type",
      accessor: "type",
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
          {value}
        </span>
      )
    },
    {
      header: "Contact",
      accessor: "contact",
      render: (value) => <span className="text-sm text-slate-600 dark:text-slate-300">{value || "N/A"}</span>
    },
    {
      header: "Referred By",
      accessor: "referBy",
      render: (value) => <span className="text-sm text-slate-600 dark:text-slate-300 font-medium">{value || "N/A"}</span>
    },
    {
      header: "Location",
      accessor: "address",
      render: (value) => {
        if (!value) return <span className="text-sm text-slate-400">N/A</span>;
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-yale-blue-600 hover:text-yale-blue-700 hover:bg-yale-blue-50 dark:text-yale-blue-400 dark:hover:bg-yale-blue-800/50"
              >
                <MapPin className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3 text-xs bg-white dark:bg-yale-blue-900 border border-slate-200 dark:border-yale-blue-800 shadow-lg rounded-lg">
              <div className="font-semibold text-yale-blue-900 dark:text-slate-100 mb-1">Contractor Address</div>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed break-words">{value}</p>
            </PopoverContent>
          </Popover>
        );
      }
    },
    {
      header: "Date Joined",
      accessor: "createdAt",
      sortable: true,
      sortKey: "createdAt",
      render: (value) => moment(value).format("DD/MM/YYYY")
    },
    {
      header: "Actions",
      filter: permissions?.canDelete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setContractorPage(0);
                }}
                id="show-deleted-contractors-table"
              />
              <Label htmlFor="show-deleted-contractors-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      cellClassName: "text-right",
      render: (_, contractor) => (
        <div className="flex justify-end gap-2">
          {permissions?.canUpdate && !contractor.deleted && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(contractor)}
              className="h-8 w-8 text-yale-blue-600 hover:text-yale-blue-700 hover:bg-yale-blue-50"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
          )}
          {permissions?.canDelete && !contractor.deleted && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(contractor.id)}
              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      )
    }
  ];

  return (
    <DataTable
      columns={columns}
      data={contractors}
      emptyStateTitle="No contractors found"
      emptyStateDescription="Add a new contractor to get started."
      emptyStateIcon={User}
      sortBy={sortBy}
      direction={direction}
      onSort={onSort}
    />
  );
};

export default ContractorTable;
